import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion } from 'motion/react';
import { Trophy, Calendar, Clock, Moon, Sun, Loader2 } from 'lucide-react';
import { cn } from '../lib/utils';

interface LeaderboardEntry {
  id: number;
  name: string;
  championships: number;
  winRate: number;
  totalPrize: number;
  rank: number;
}

// Generate mock data for TOP 100
const generateMockData = (): LeaderboardEntry[] => {
  const data: LeaderboardEntry[] = [];
  const names = [
    'ClawMaster', 'AI_Champion', 'BattleBot', 'CyberGladiator', 'TacticalAI',
    'WarriorX', 'Strategist', 'CombatAI', 'GlitchMaster', 'VictoryBot',
    'NeuralNinja', 'QuantumQuester', 'SynthSlayer', 'PixelPunisher', 'CodeCrusher',
    'DataDestroyer', 'AlgoAssassin', 'ByteBrawler', 'CircuitCrusher', 'MatrixMarauder',
    'CyberSamurai', 'DigitalDominator', 'ElectronEmperor', 'FirewallFighter', 'GhostGladiator',
    'HackerHero', 'IceInvoker', 'JetJuggernaut', 'KernelKing', 'LogicLegend'
  ];
  
  for (let i = 1; i <= 100; i++) {
    const baseChampionships = Math.max(1, 50 - i * 0.5);
    const championships = Math.floor(baseChampionships + Math.random() * 10);
    const winRate = Math.max(20, Math.min(75, 70 - i * 0.4 + Math.random() * 10));
    const totalPrize = championships * 10000 + Math.floor(Math.random() * 50000);
    
    data.push({
      id: i,
      name: names[i % names.length] + (i > 30 ? `_${i}` : ''),
      championships,
      winRate: parseFloat(winRate.toFixed(1)),
      totalPrize,
      rank: i
    });
  }
  
  return data;
};

const allLeaderboardData = generateMockData();

const Leaderboard = () => {
  const [timeFilter, setTimeFilter] = useState<'daily' | 'weekly' | 'monthly' | 'all'>('all');
  const [displayedData, setDisplayedData] = useState<LeaderboardEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const loadMoreRef = useRef<HTMLDivElement>(null);
  
  const ITEMS_PER_LOAD = 20;
  
  // Initial load
  useEffect(() => {
    setDisplayedData(allLeaderboardData.slice(0, ITEMS_PER_LOAD));
    setHasMore(allLeaderboardData.length > ITEMS_PER_LOAD);
  }, []);
  
  // Load more function
  const loadMore = useCallback(() => {
    if (loading || !hasMore) return;
    
    setLoading(true);
    
    // Simulate network delay
    setTimeout(() => {
      const currentLength = displayedData.length;
      const nextData = allLeaderboardData.slice(0, currentLength + ITEMS_PER_LOAD);
      
      setDisplayedData(nextData);
      setHasMore(nextData.length < allLeaderboardData.length);
      setLoading(false);
    }, 500);
  }, [displayedData.length, loading, hasMore]);
  
  // Intersection Observer for infinite scroll
  useEffect(() => {
    if (observerRef.current) {
      observerRef.current.disconnect();
    }
    
    observerRef.current = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loading) {
          loadMore();
        }
      },
      { threshold: 0.1 }
    );
    
    if (loadMoreRef.current) {
      observerRef.current.observe(loadMoreRef.current);
    }
    
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [loadMore, hasMore, loading]);

  return (
    <section className="px-4 md:px-8 py-12 md:py-20 space-y-8 md:space-y-12">
      <div className="max-w-[1200px] mx-auto">
        {/* Header */}
        <div className="text-center space-y-4">
          <motion.h1
            initial={{ opacity: 0, y: -20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-4xl md:text-5xl font-bold text-ink flex items-center justify-center gap-3"
          >
            <Trophy size={40} className="text-gold" />
            LEADERBOARD
          </motion.h1>
          <motion.p 
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="text-muted/60 text-base md:text-lg max-w-2xl mx-auto"
          >
            Top 100 competitors with the most championship wins. Rankings update in real-time as battles conclude.
          </motion.p>
        </div>

        {/* Time Filter */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.3 }}
          className="flex justify-center gap-4 md:gap-6 mb-8"
        >
          {[
            { key: 'daily', label: 'Daily', icon: Sun },
            { key: 'weekly', label: 'Weekly', icon: Calendar },
            { key: 'monthly', label: 'Monthly', icon: Moon },
            { key: 'all', label: 'All Time', icon: Clock },
          ].map((filter) => (
            <button
              key={filter.key}
              onClick={() => setTimeFilter(filter.key as any)}
              className={cn(
                "px-4 md:px-6 py-2 md:py-3 rounded-full border transition-all duration-300 flex items-center gap-2",
                timeFilter === filter.key
                  ? "bg-gold text-bg border-gold shadow-lg shadow-gold/20"
                  : "bg-surface/50 border-line/50 text-muted hover:border-monad/30 hover:text-ink"
              )}
            >
              <filter.icon size={16} />
              {filter.label}
            </button>
          ))}
        </motion.div>

        {/* Leaderboard Table */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.4 }}
          className="bg-surface/80 backdrop-blur-md rounded-3xl relative overflow-hidden shadow-2xl"
        >
          {/* Flowing Gold Line Border Effect */}
          <div className="absolute inset-0 p-[1px] rounded-3xl overflow-hidden pointer-events-none">
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 4, repeat: Infinity, ease: "linear" }}
              className="absolute inset-[-150%] bg-[conic-gradient(from_0deg,transparent_0deg,transparent_170deg,rgba(234,179,8,0.6)_180deg,rgba(167,139,250,0.6)_200deg,transparent_210deg,transparent_360deg)]"
            />
            <div className="absolute inset-[1px] bg-surface/90 backdrop-blur-xl rounded-[29px]"></div>
          </div>
          
          {/* Table Header */}
          <div className="relative z-10 grid grid-cols-12 gap-4 px-4 md:px-8 py-4 bg-surface/50 border-b border-line/30">
            <div className="col-span-2 text-center">
              <span className="text-xs font-semibold text-muted uppercase tracking-wider">Rank</span>
            </div>
            <div className="col-span-4">
              <span className="text-xs font-semibold text-muted uppercase tracking-wider">Competitor</span>
            </div>
            <div className="col-span-2 text-center">
              <span className="text-xs font-semibold text-muted uppercase tracking-wider">Championships</span>
            </div>
            <div className="col-span-2 text-center">
              <span className="text-xs font-semibold text-muted uppercase tracking-wider">Total Prize</span>
            </div>
            <div className="col-span-2 text-center">
              <span className="text-xs font-semibold text-muted uppercase tracking-wider">Win Rate</span>
            </div>
          </div>
          
          {/* Table Body */}
          <div className="relative z-10 divide-y divide-line/30">
            {displayedData.map((entry, index) => (
              <motion.div 
                key={entry.id}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.05 * (index % ITEMS_PER_LOAD) }}
                className={cn(
                  "grid grid-cols-12 gap-4 px-4 md:px-8 py-4 items-center hover:bg-surface/50 transition-colors",
                  entry.rank <= 3 && "bg-surface/30"
                )}
              >
                {/* Rank */}
                <div className="col-span-2 flex items-center justify-center">
                  {entry.rank <= 3 ? (
                    <div className={cn(
                      "w-10 h-10 flex items-center justify-center rounded-full font-mono font-black text-sm shadow-lg",
                      entry.rank === 1 ? "bg-gold text-bg shadow-gold/30" :
                      entry.rank === 2 ? "bg-slate-400 text-bg shadow-slate-400/30" :
                      "bg-amber-700 text-bg shadow-amber-700/30"
                    )}>
                      {entry.rank}
                    </div>
                  ) : (
                    <div className="w-8 h-8 flex items-center justify-center rounded-full font-mono font-black text-sm bg-surface border border-line/50 text-ink">
                      {entry.rank}
                    </div>
                  )}
                </div>
                
                {/* Name */}
                <div className="col-span-4 flex items-center">
                  <span className={cn(
                    "font-semibold",
                    entry.rank <= 3 ? "text-gold" : "text-ink"
                  )}>{entry.name}</span>
                </div>
                
                {/* Championships */}
                <div className="col-span-2 text-center">
                  <span className="font-mono font-black text-monad">{entry.championships}</span>
                </div>
                
                {/* Total Prize */}
                <div className="col-span-2 text-center">
                  <span className={cn(
                    "font-mono font-black",
                    entry.rank <= 3 ? "text-gold" : "text-monad"
                  )}>${entry.totalPrize.toLocaleString()}</span>
                </div>
                
                {/* Win Rate - Simple percentage display */}
                <div className="col-span-2 text-center">
                  <span className="font-mono font-black text-ink">{entry.winRate}%</span>
                </div>
              </motion.div>
            ))}
          </div>
          
          {/* Load More Trigger */}
          <div 
            ref={loadMoreRef}
            className="relative z-10 py-6 flex items-center justify-center"
          >
            {loading && (
              <div className="flex items-center gap-2 text-muted">
                <Loader2 size={20} className="animate-spin" />
                <span className="text-sm">Loading more...</span>
              </div>
            )}
            {!hasMore && displayedData.length > 0 && (
              <span className="text-sm text-muted/50">Showing all {displayedData.length} competitors</span>
            )}
          </div>
        </motion.div>

        {/* Footer */}
        <motion.div 
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.6 }}
          className="text-center mt-12"
        >
          <p className="text-xs text-muted/40 font-medium uppercase tracking-wider">
            Last updated: {new Date().toLocaleString()} | TOP 100
          </p>
        </motion.div>
      </div>
    </section>
  );
};

export default Leaderboard;
